import styled from 'styled-components';
import { theme } from './theme';

// Componentes base estilizados
export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing[4]};
`;

export const Flex = styled.div.withConfig({
  shouldForwardProp: (prop) => !['direction', 'justify', 'align', 'wrap', 'gap'].includes(prop as string),
})<{
  direction?: 'row' | 'column';
  justify?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
  align?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  gap?: keyof typeof theme.spacing;
}>`
  display: flex;
  flex-direction: ${props => props.direction || 'row'};
  justify-content: ${props => props.justify || 'flex-start'};
  align-items: ${props => props.align || 'stretch'};
  flex-wrap: ${props => props.wrap || 'nowrap'};
  gap: ${props => theme.spacing[props.gap || 4]};
`;

export const Grid = styled.div.withConfig({
  shouldForwardProp: (prop) => !['columns', 'gap', 'minHeight'].includes(prop as string),
})<{
  columns?: number;
  gap?: keyof typeof theme.spacing;
  minHeight?: string;
}>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 1}, 1fr);
  gap: ${props => theme.spacing[props.gap || 4]};
  min-height: ${props => props.minHeight || 'auto'};
`;

export const Card = styled.div.withConfig({
  shouldForwardProp: (prop) => !['padding', 'glow', 'hover'].includes(prop as string),
})<{
  padding?: keyof typeof theme.spacing;
  glow?: boolean;
  hover?: boolean;
}>`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[props.padding || 6]};
  box-shadow: ${props => props.glow ? props.theme.shadows.neon : props.theme.shadows.md};
  transition: all ${props => props.theme.animations.duration.normal} ${props => props.theme.animations.easing.easeInOut};
  
  ${props => props.hover && `
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${props.theme.shadows.lg};
      border-color: ${props.theme.colors.primary};
    }
  `}
`;

export const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => !['variant', 'size', 'fullWidth', 'glow'].includes(prop as string),
})<{
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  glow?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  font-family: ${theme.typography.fontFamily.heading};
  font-weight: ${theme.typography.fontWeight.medium};
  border-radius: ${theme.borders.radius.md};
  transition: all ${theme.animations.duration.fast} ${theme.animations.easing.easeInOut};
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  ${props => {
    switch (props.size) {
      case 'sm':
        return `
          padding: ${theme.spacing[2]} ${theme.spacing[4]};
          font-size: ${theme.typography.fontSize.sm};
        `;
      case 'lg':
        return `
          padding: ${theme.spacing[4]} ${theme.spacing[8]};
          font-size: ${theme.typography.fontSize.lg};
        `;
      default:
        return `
          padding: ${theme.spacing[3]} ${theme.spacing[6]};
          font-size: ${theme.typography.fontSize.base};
        `;
    }
  }}
  
  ${props => props.fullWidth && 'width: 100%;'}
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${theme.colors.gradientPrimary};
          color: ${theme.colors.textPrimary};
          border: 1px solid ${theme.colors.primary};
          
          &:hover {
            background: ${theme.colors.primaryHover};
            transform: translateY(-1px);
            box-shadow: ${theme.shadows.neon};
          }
          
          &:active {
            transform: translateY(0);
          }
        `;
      case 'secondary':
        return `
          background: ${theme.colors.backgroundTertiary};
          color: ${theme.colors.textPrimary};
          border: 1px solid ${theme.colors.border};
          
          &:hover {
            background: ${theme.colors.backgroundSecondary};
            border-color: ${theme.colors.primary};
          }
        `;
      case 'ghost':
        return `
          background: transparent;
          color: ${theme.colors.textSecondary};
          border: 1px solid transparent;
          
          &:hover {
            background: ${theme.colors.backgroundSecondary};
            color: ${theme.colors.textPrimary};
          }
        `;
      case 'danger':
        return `
          background: ${theme.colors.error};
          color: ${theme.colors.textPrimary};
          border: 1px solid ${theme.colors.error};
          
          &:hover {
            background: ${theme.colors.primaryDark};
            transform: translateY(-1px);
          }
        `;
      default:
        return `
          background: ${theme.colors.backgroundTertiary};
          color: ${theme.colors.textPrimary};
          border: 1px solid ${theme.colors.border};
          
          &:hover {
            background: ${theme.colors.backgroundSecondary};
            border-color: ${theme.colors.primary};
          }
        `;
    }
  }}
  
  ${props => props.glow && `
    box-shadow: ${theme.shadows.neon};
    animation: glow 2s ease-in-out infinite alternate;
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

export const Input = styled.input.withConfig({
  shouldForwardProp: (prop) => !['error', 'fullWidth'].includes(prop as string),
})<{
  error?: boolean;
  fullWidth?: boolean;
}>`
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.error ? theme.colors.error : theme.colors.border};
  border-radius: ${theme.borders.radius.md};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.base};
  transition: all ${theme.animations.duration.fast} ${theme.animations.easing.easeInOut};
  
  &:focus {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.textTertiary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Textarea = styled.textarea.withConfig({
  shouldForwardProp: (prop) => !['error', 'fullWidth', 'rows'].includes(prop as string),
})<{
  error?: boolean;
  fullWidth?: boolean;
  rows?: number;
}>`
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  min-height: ${props => props.rows ? `${props.rows * 1.5}rem` : '6rem'};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.error ? theme.colors.error : theme.colors.border};
  border-radius: ${theme.borders.radius.md};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily.primary};
  resize: vertical;
  transition: all ${theme.animations.duration.fast} ${theme.animations.easing.easeInOut};
  
  &:focus {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.textTertiary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Select = styled.select.withConfig({
  shouldForwardProp: (prop) => !['error', 'fullWidth'].includes(prop as string),
})<{
  error?: boolean;
  fullWidth?: boolean;
}>`
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.error ? theme.colors.error : theme.colors.border};
  border-radius: ${theme.borders.radius.md};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.base};
  cursor: pointer;
  transition: all ${theme.animations.duration.fast} ${theme.animations.easing.easeInOut};
  
  &:focus {
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  option {
    background: ${theme.colors.backgroundSecondary};
    color: ${theme.colors.textPrimary};
  }
`;

export const Label = styled.label.withConfig({
  shouldForwardProp: (prop) => !['required', 'error'].includes(prop as string),
})<{
  required?: boolean;
  error?: boolean;
}>`
  display: block;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${props => props.error ? theme.colors.error : theme.colors.textSecondary};
  margin-bottom: ${theme.spacing[2]};
  
  ${props => props.required && `
    &::after {
      content: ' *';
      color: ${theme.colors.error};
    }
  `}
`;

export const Badge = styled.span.withConfig({
  shouldForwardProp: (prop) => !['variant', 'size'].includes(prop as string),
})<{
  variant?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  size?: 'sm' | 'md' | 'lg';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => {
    switch (props.size) {
      case 'sm': return theme.typography.fontSize.xs;
      case 'lg': return theme.typography.fontSize.base;
      default: return theme.typography.fontSize.sm;
    }
  }};
  font-weight: ${theme.typography.fontWeight.medium};
  padding: ${props => {
    switch (props.size) {
      case 'sm': return `${theme.spacing[1]} ${theme.spacing[2]}`;
      case 'lg': return `${theme.spacing[2]} ${theme.spacing[4]}`;
      default: return `${theme.spacing[1]} ${theme.spacing[3]}`;
    }
  }};
  border-radius: ${theme.borders.radius.full};
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${theme.colors.primary};
          color: ${theme.colors.textPrimary};
        `;
      case 'success':
        return `
          background: ${theme.colors.success};
          color: ${theme.colors.background};
        `;
      case 'warning':
        return `
          background: ${theme.colors.warning};
          color: ${theme.colors.background};
        `;
      case 'error':
        return `
          background: ${theme.colors.error};
          color: ${theme.colors.textPrimary};
        `;
      case 'info':
        return `
          background: ${theme.colors.info};
          color: ${theme.colors.textPrimary};
        `;
      default:
        return `
          background: ${theme.colors.backgroundTertiary};
          color: ${theme.colors.textSecondary};
        `;
    }
  }}
`;

export const Divider = styled.hr.withConfig({
  shouldForwardProp: (prop) => !['orientation', 'thickness'].includes(prop as string),
})<{
  orientation?: 'horizontal' | 'vertical';
  thickness?: keyof typeof theme.borders.width;
}>`
  border: none;
  background: ${theme.colors.border};
  
  ${props => {
    if (props.orientation === 'vertical') {
      return `
        width: ${theme.borders.width[props.thickness || 'thin']};
        height: 100%;
      `;
    } else {
      return `
        height: ${theme.borders.width[props.thickness || 'thin']};
        width: 100%;
      `;
    }
  }}
`;

export const LoadingSpinner = styled.div.withConfig({
  shouldForwardProp: (prop) => !['size', 'color'].includes(prop as string),
})<{
  size?: 'sm' | 'md' | 'lg';
  color?: string;
}>`
  width: ${props => {
    switch (props.size) {
      case 'sm': return '16px';
      case 'lg': return '32px';
      default: return '24px';
    }
  }};
  height: ${props => {
    switch (props.size) {
      case 'sm': return '16px';
      case 'lg': return '32px';
      default: return '24px';
    }
  }};
  border: 2px solid ${theme.colors.border};
  border-top: 2px solid ${props => props.color || theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
`;

export const Tooltip = styled.div.withConfig({
  shouldForwardProp: (prop) => !['position', 'visible'].includes(prop as string),
})<{
  position?: 'top' | 'bottom' | 'left' | 'right';
  visible?: boolean;
}>`
  position: absolute;
  z-index: 1000;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.backgroundTertiary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borders.radius.md};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  box-shadow: ${theme.shadows.lg};
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: all ${theme.animations.duration.fast} ${theme.animations.easing.easeInOut};
  
  ${props => {
    switch (props.position) {
      case 'top':
        return `
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: ${theme.spacing[2]};
        `;
      case 'bottom':
        return `
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-top: ${theme.spacing[2]};
        `;
      case 'left':
        return `
          right: 100%;
          top: 50%;
          transform: translateY(-50%);
          margin-right: ${theme.spacing[2]};
        `;
      case 'right':
        return `
          left: 100%;
          top: 50%;
          transform: translateY(-50%);
          margin-left: ${theme.spacing[2]};
        `;
      default:
        return `
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          margin-bottom: ${theme.spacing[2]};
        `;
    }
  }}
  
  &::before {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border: 4px solid transparent;
    
    ${props => {
      switch (props.position) {
        case 'top':
          return `
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-top-color: ${theme.colors.border};
          `;
        case 'bottom':
          return `
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-bottom-color: ${theme.colors.border};
          `;
        case 'left':
          return `
            left: 100%;
            top: 50%;
            transform: translateY(-50%);
            border-left-color: ${theme.colors.border};
          `;
        case 'right':
          return `
            right: 100%;
            top: 50%;
            transform: translateY(-50%);
            border-right-color: ${theme.colors.border};
          `;
        default:
          return `
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-top-color: ${theme.colors.border};
          `;
      }
    }}
  }
`;

export const Modal = styled.div.withConfig({
  shouldForwardProp: (prop) => !['visible'].includes(prop as string),
})<{
  visible?: boolean;
}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.overlay};
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: all ${theme.animations.duration.normal} ${theme.animations.easing.easeInOut};
`;

export const ModalContent = styled.div`
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borders.radius.lg};
  box-shadow: ${theme.shadows.xl};
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  animation: fadeIn ${theme.animations.duration.normal} ${theme.animations.easing.easeInOut};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borders.radius.lg};
  overflow: hidden;
  box-shadow: ${theme.shadows.md};
`;

export const TableHeader = styled.thead`
  background: ${theme.colors.backgroundTertiary};
`;

export const TableBody = styled.tbody``;

export const TableRow = styled.tr.withConfig({
  shouldForwardProp: (prop) => !['hover'].includes(prop as string),
})<{
  hover?: boolean;
}>`
  border-bottom: 1px solid ${theme.colors.border};
  transition: background-color ${theme.animations.duration.fast} ${theme.animations.easing.easeInOut};
  
  ${props => props.hover && `
    &:hover {
      background: ${theme.colors.backgroundTertiary};
    }
  `}
  
  &:last-child {
    border-bottom: none;
  }
`;

export const TableCell = styled.td.withConfig({
  shouldForwardProp: (prop) => !['header', 'align'].includes(prop as string),
})<{
  header?: boolean;
  align?: 'left' | 'center' | 'right';
}>`
  padding: ${theme.spacing[4]};
  text-align: ${props => props.align || 'left'};
  font-weight: ${props => props.header ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal};
  color: ${props => props.header ? theme.colors.textPrimary : theme.colors.textSecondary};
  border-right: 1px solid ${theme.colors.border};
  
  &:last-child {
    border-right: none;
  }
`;

export const TableHeaderCell = styled.th.withConfig({
  shouldForwardProp: (prop) => !['align'].includes(prop as string),
})<{
  align?: 'left' | 'center' | 'right';
}>`
  padding: ${theme.spacing[4]};
  text-align: ${props => props.align || 'left'};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  border-right: 1px solid ${theme.colors.border};
  
  &:last-child {
    border-right: none;
  }
`;

